import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  TemplateRef,
  Type,
  untracked,
  viewChild,
} from '@angular/core';
import {
  FormValueControl,
  ValidationError,
  WithOptionalFieldTree,
} from '@angular/forms/signals';
import { handleRequestError } from '@softline/application';
import { Subscription } from 'rxjs';
import {
  FavoriteBookmarkBody,
  FavoriteEntityBookmarkRepository,
  LastSearchBookmarkRepository,
  LastViewedEntityBookmarkBody,
  LastViewedEntityBookmarkRepository,
} from '@softapps/core';
import { MASTERDATA_DEFAULT_PAGE_SIZE } from '../../functions/masterdata-query.function';
import { MasterdataEntityRegistry } from '../../services/masterdata-entity-registry.service';
import { MasterdataService } from '../../services/masterdata.service';
import { MasterdataSearchSettingsService } from '../../services/masterdata-search-settings.service';
import {
  MasterdataAvatar,
  MasterdataBadge,
  MasterdataEntityDefinition,
  MasterdataFilter,
  MasterdataFromBookmark,
  MasterdataInputMode,
  MasterdataInputPresentation,
} from '../../types';
import { SearchDropdownComponent } from '../search-dropdown/search-dropdown.component';
import { HighlightMatchPipe } from '../../pipes/highlight-match.pipe';
import { BadgeComponent, SearchInputSize } from '@softline/ui-core';
import {
  SelectDsComponent,
  SelectDsItem,
  SelectDsMultiComponent,
} from '@softline/ui-core/select';

const SELECT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Generic masterdata search input — the single component for every masterdata
 * entity, driven by a {@link MasterdataEntityDefinition} constant or a registered
 * entity name. Implements `FormValueControl`, binding via `[(value)]` or `[control]`.
 */
@Component({
  selector: 'soft-masterdata-input',
  imports: [
    SearchDropdownComponent,
    HighlightMatchPipe,
    BadgeComponent,
    SelectDsComponent,
    SelectDsMultiComponent,
  ],
  templateUrl: './masterdata-input.component.html',
  styleUrl: './masterdata-input.component.css',
  providers: [
    {
      provide: LastSearchBookmarkRepository,
      useFactory: () =>
        new LastSearchBookmarkRepository(inject(MasterdataInputComponent).subCategory()),
    },
    {
      provide: LastViewedEntityBookmarkRepository,
      useFactory: () =>
        new LastViewedEntityBookmarkRepository(
          inject(MasterdataInputComponent).subCategory()
        ),
    },
    {
      provide: FavoriteEntityBookmarkRepository,
      useFactory: () =>
        new FavoriteEntityBookmarkRepository(
          inject(MasterdataInputComponent).subCategory()
        ),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.width]': 'hostWidth()',
    '[style.minWidth]': 'hostMinWidth()',
    '[class.inline]': 'inline()',
    '[class.presentation-row]': 'isRow()',
    '[class.soft-ds-form-row]': 'isRow()',
  },
})
export class MasterdataInputComponent<
  TDto extends object,
> implements FormValueControl<TDto | null> {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly masterdata = inject(MasterdataService);
  private readonly registry = inject(MasterdataEntityRegistry);
  private readonly injector = inject(Injector);
  private readonly searchSettings = inject(MasterdataSearchSettingsService);

  private readonly searchDropdown = viewChild(SearchDropdownComponent);

  readonly openDropdownOnFocus = this.searchSettings.openDropdownOnFocus;

  /** Opens the extended (F4) search overlay — the programmatic twin of the F4
   *  key in the field. No-op in `select` mode. */
  public openExtendedSearch(): void {
    this.searchDropdown()?.openExpanded();
  }

  /** Drops the typed search term — for hosts that consume the term themselves
   *  (e.g. a free-text e-mail typed into a contact lookup). */
  public clearSearchTerm(): void {
    this.searchDropdown()?._value.set('');
  }

  /** Entity to search: a definition constant (typed) or a registered name. */
  readonly entity = input.required<MasterdataEntityDefinition<TDto> | string>();
  /** Rendering mode: `search` (field + dropdown + F4 overlay) or `select` (a
   *  `soft-ds-select` over the records). `null` (default) takes the definition's `mode`. */
  readonly mode = input<MasterdataInputMode | null>(null);
  /** Select mode only (ignored otherwise, and in `multiple` mode): loads the entity's
   *  default result list eagerly on init instead of waiting for the dropdown to open,
   *  and auto-picks the first result once loaded if nothing is selected yet. */
  readonly autoSelectFirst = input(false, { transform: booleanAttribute });
  /** Field presentation: `default` (label above the field) or `row` — a
   *  `soft-ds-field-row` for mobile grouped lists; the editor opens as a sheet. */
  readonly presentation = input<MasterdataInputPresentation>('default');
  protected readonly isRow = computed(
    () => this.presentation() === 'row' && !this.inline()
  );
  readonly value = model<TDto | null>(null);
  /** Fill the field from an id alone: fetches the record via the entity's query
   *  endpoint (definition `idFilter`, default: equals on `id`) and writes `value`. */
  readonly entityId = input<string | number | null>(null);
  readonly label = input<string>('');
  readonly showLabel = input<boolean>(true);
  readonly readonly = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly size = input<SearchInputSize>('md');
  readonly required = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly errors = input<readonly WithOptionalFieldTree<ValidationError>[]>([]);
  readonly invalid = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly overlayTitle = input<string | null>(null);
  readonly placeholder = input<string | null>(null);
  /** Structural filter AND'ed into every search, on top of the definition's defaultFilter. */
  readonly filter = input<MasterdataFilter | null>(null);

  readonly resultTemplate = input<TemplateRef<{ $implicit: TDto }> | null>(null);
  readonly selectedTemplate = input<TemplateRef<{ $implicit: TDto }> | null>(null);
  readonly avatarWith = input<((entity: TDto) => MasterdataAvatar) | null>(null);
  /** Grow the field to the selected label (clamped) — for compact contexts. */
  readonly autoWidth = input(false, { transform: booleanAttribute });
  readonly autoFocus = input(false, { transform: booleanAttribute });
  /** Clear the field after every pick (entry-bar reuse) instead of keeping the record shown. */
  readonly clearOnPick = input(false, { transform: booleanAttribute });
  /** Render the extended search directly in the page instead of a trigger + overlay. */
  readonly inline = input(false, { transform: booleanAttribute });
  /** Prefills the field and runs the search once the dropdown is available;
   *  a later change to a new non-empty term re-seeds and searches again. */
  readonly initialSearchTerm = input<string | null>(null);
  readonly searchTerm = computed(() => this.searchDropdown()?.searchTerm() ?? '');
  /** Multiselect mode: rows toggle membership in {@link values}; the apply
   *  ("übernehmen") action commits the array via {@link pickedMany}. */
  readonly multiple = input(false, { transform: booleanAttribute });

  /** Offer the on-the-fly multi-select ("Mehrfachauswahl") toggle in the expanded
   *  search — only for hosts that handle `pickedMany`. */
  readonly multiToggle = input(false, { transform: booleanAttribute });

  /** Arm the Mehrfachauswahl automatically each time the extended search opens
   *  (like the Swing WebView's Artikelabfrage); requires `multiToggle`. */
  readonly multiToggleDefault = input(false, { transform: booleanAttribute });

  /** Render the clickable F3 slot for hosts owning a special-query ("Spezialabfrage") dialog. */
  readonly specialQueryKey = input(false, { transform: booleanAttribute });
  readonly values = model<TDto[]>([]);
  /** Emitted once when the multiselect apply ("übernehmen") commits the checked array. */
  readonly pickedMany = output<TDto[]>();

  /** Optional custom bookmark mappers (default: JSON-stash the whole record). */
  readonly toFavoriteBody = input<((entity: TDto) => FavoriteBookmarkBody) | null>(null);
  readonly toLastViewedBody = input<
    ((entity: TDto) => LastViewedEntityBookmarkBody) | null
  >(null);
  readonly fromBookmarkBody = input<MasterdataFromBookmark<TDto> | null>(null);

  /** Emitted on every non-null pick (in addition to the value model). */
  readonly picked = output<TDto>();

  readonly definition = computed<MasterdataEntityDefinition<TDto>>(() => {
    const entity = this.entity();
    return typeof entity === 'string'
      ? (this.registry.get(entity) as MasterdataEntityDefinition<TDto>)
      : entity;
  });

  private readonly detailPanel = computed(() =>
    this.registry.detailPanel(this.definition().name)
  );
  /** The definition's own panel first, then one contributed by the app. */
  protected readonly detailComponent = computed<Type<unknown> | null>(
    () => this.definition().detailComponent?.() ?? this.detailPanel()?.component() ?? null
  );
  protected readonly detailInputs = computed(
    () => this.definition().detailInputs ?? this.detailPanel()?.inputs ?? null
  );

  readonly effectiveMode = computed<MasterdataInputMode>(
    () => this.mode() ?? this.definition().mode ?? 'search'
  );
  protected readonly selectMode = computed(() => this.effectiveMode() === 'select');

  /** Opt out of the bookmark features even for a bookmark-capable entity. */
  readonly bookmarks = input(true, { transform: booleanAttribute });

  /** Bookmarks require a definition `bookmarkSubCategory` — the backend accepts
   *  only a fixed set of subCategory values. */
  readonly bookmarksEnabled = computed(
    () => this.bookmarks() && !!this.definition().bookmarkSubCategory
  );

  readonly subCategory = computed(
    () => this.definition().bookmarkSubCategory ?? this.definition().name
  );

  protected readonly hostWidth = computed<string | null>(() => {
    if (!this.autoWidth()) return null;
    const value = this.value();
    if (!value) return '22rem';
    const len = this.displayEntity(value).length;
    return `clamp(18rem, calc(${(len * 0.75).toFixed(1)}ch + 4rem), 40rem)`;
  });

  /** Lets the field shrink below its preferred width inside a flex row. */
  protected readonly hostMinWidth = computed<string | null>(() =>
    this.autoWidth() ? 'min(12rem, 100%)' : null
  );

  readonly searchResults = signal<TDto[] | null>(null);
  readonly loading = signal(false);
  readonly totalCount = signal<number | null>(null);
  readonly hasMore = signal(false);
  readonly loadingMore = signal(false);
  /** True when the last first-page search failed — drives the overlay's error state. */
  readonly error = signal(false);

  private currentTerm: string | null | undefined = '';
  private offset = 0;
  private searchSubscription: Subscription | null = null;
  private loadMoreSubscription: Subscription | null = null;
  private selectSearchHandle: ReturnType<typeof setTimeout> | null = null;

  /** Guards `[entityId]` resolves against races (only the latest wins). */
  private resolveIdSeq = 0;

  /** Guards `autoSelectFirst` against re-arming on every result load. */
  private autoSelectArmed = false;

  constructor() {
    let previousKey: string | null = null;
    effect(() => {
      const key = `${this.definition().name}|${JSON.stringify(this.filter() ?? null)}`;
      if (previousKey !== null && previousKey !== key) this.resetSearchState();
      previousKey = key;
    });
    effect(() => {
      const definition = this.definition();
      untracked(() => definition.init?.(this.injector));
    });
    effect(() => {
      const id = this.entityId();
      const definition = this.definition();
      if (id == null) return;
      const current = untracked(() => this.value());
      if (current != null && this.entityKey(current) === String(id)) return;
      const seq = ++this.resolveIdSeq;
      untracked(() => void this.resolveEntityId(definition, id, seq));
    });
    effect(() => {
      if (!this.autoSelectFirst() || !this.selectMode() || this.multiple()) return;
      const results = this.searchResults(); // always read, so resetSearchState() re-arms us
      if (this.autoSelectArmed) return;
      if (results === null) {
        untracked(() => {
          if (!this.loading()) this.searchFn('');
        });
        return;
      }
      this.autoSelectArmed = true;
      if (results.length === 0) return;
      untracked(() => {
        if (this.value() == null) this.onSelected(results[0]);
      });
    });
    afterNextRender(() => {
      if (this.autoFocus()) this.focus();
    });
    let seededTerm: string | null = null;
    effect(() => {
      const dropdown = this.searchDropdown();
      const term = this.initialSearchTerm();
      if (!dropdown || !term || term === seededTerm) return;
      seededTerm = term;
      untracked(() => {
        if (term === dropdown.searchTerm()) return;
        dropdown.setInputValue(term);
        dropdown.search();
        dropdown.focusResults();
      });
    });
    inject(DestroyRef).onDestroy(() => {
      this.clearSelectSearchDebounce();
      this.abortInFlightSearch();
    });
  }

  readonly displayEntity = (entity: TDto): string =>
    entity == null ? '' : this.definition().displayWith(entity);

  /** The typeable key of a record, for direct exact-key selection on Tab/Enter. */
  readonly keyOf = (entity: TDto): string | number | null | undefined => {
    if (entity == null) return null;
    const def = this.definition();
    if (def.keyWith) return def.keyWith(entity);
    const e = entity as Record<string, any>;
    return (
      e['key'] ??
      e['number'] ??
      e['account']?.number ??
      def.trackWith?.(entity) ??
      e['id']
    );
  };

  readonly trackOf = (entity: TDto): string | number =>
    this.definition().trackWith?.(entity) ?? this.entityKey(entity);

  readonly secondaryOf = (entity: TDto): string | null =>
    this.definition().secondaryWith?.(entity) ?? null;

  /** Name only (no key prefix) for the table's entity cell; falls back to the display text. */
  readonly nameOf = (entity: TDto): string | null =>
    entity == null
      ? null
      : (this.definition().nameWith?.(entity) ?? this.definition().displayWith(entity));

  readonly unitOf = (entity: TDto): string | null =>
    this.definition().unitWith?.(entity) ?? null;

  readonly badgeOf = (entity: TDto): MasterdataBadge | null =>
    this.definition().badgeWith?.(entity) ?? null;

  readonly badgesOf = (entity: TDto): MasterdataBadge[] => {
    const definition = this.definition();
    if (definition.badgesWith) return definition.badgesWith(entity);
    const badge = definition.badgeWith?.(entity) ?? null;
    return badge ? [badge] : [];
  };

  badgeVariant(badge: MasterdataBadge): 'success' | 'warning' | 'danger' | 'secondary' {
    switch (badge.tone) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  readonly searchFn = (searchTerm: string | null | undefined): void => {
    this.currentTerm = searchTerm;
    this.offset = 0;
    this.abortInFlightSearch();
    this.error.set(false);
    this.loading.set(true);
    this.searchSubscription = this.masterdata
      .searchWithCount$(this.definition(), searchTerm, {
        filter: this.filter(),
        limit: MASTERDATA_DEFAULT_PAGE_SIZE,
        offset: 0,
      })
      .subscribe({
        next: ({ items, totalCount }) => {
          this.searchResults.set(items);
          this.totalCount.set(totalCount);
          this.offset = items.length;
          this.hasMore.set(items.length < totalCount);
          this.loading.set(false);
        },
        error: (e) => {
          this.searchResults.set(null);
          this.totalCount.set(null);
          this.hasMore.set(false);
          this.loading.set(false);
          this.error.set(true);
          handleRequestError(e, this.injector, { show: true });
        },
      });
  };

  readonly retrySearch = (): void => this.searchFn(this.currentTerm);

  readonly abortInFlightSearch = (): void => {
    this.searchSubscription?.unsubscribe();
    this.searchSubscription = null;
    this.loadMoreSubscription?.unsubscribe();
    this.loadMoreSubscription = null;
    this.loadingMore.set(false);
  };

  onLoadMore(): void {
    if (this.loadingMore() || !this.hasMore()) return;
    const offset = this.offset;
    this.loadingMore.set(true);
    this.loadMoreSubscription?.unsubscribe();
    this.loadMoreSubscription = this.masterdata
      .searchWithCount$(this.definition(), this.currentTerm, {
        filter: this.filter(),
        limit: MASTERDATA_DEFAULT_PAGE_SIZE,
        offset,
      })
      .subscribe({
        next: ({ items, totalCount }) => {
          this.searchResults.update((current) => [...(current ?? []), ...items]);
          this.totalCount.set(totalCount);
          this.offset = offset + items.length;
          this.hasMore.set(this.offset < totalCount);
          this.loadingMore.set(false);
        },
        error: (e) => {
          this.loadingMore.set(false);
          handleRequestError(e, this.injector, { show: true });
        },
      });
  }

  onSelected(entity: TDto | null): void {
    if (entity != null) this.picked.emit(entity);
    this.value.set(entity != null && this.clearOnPick() ? null : entity);
  }

  onConfirmedMany(entities: TDto[]): void {
    this.pickedMany.emit(entities);
    this.values.set(this.clearOnPick() ? [] : entities);
  }

  /** Reset value(s), term, highlight, preview and multiselect picks to the fresh state. */
  clear(): void {
    this.value.set(null);
    this.values.set([]);
    this.searchDropdown()?.clearSearch();
  }

  /** Replace the current term and run the search — a host-driven query restart.
   *  A seeded term means the user comes for the results: the focus moves into
   *  the table (row 0 highlighted) instead of the search field. */
  setSearchTerm(term: string): void {
    const dropdown = this.searchDropdown();
    if (!dropdown) return;
    dropdown.setInputValue(term);
    dropdown.search();
    dropdown.focusResults();
  }

  /** Close the open dropdown/modal surface without touching the term. */
  closeSearchSurfaces(): void {
    this.searchDropdown()?.closeSurfaces();
  }

  /** Select-mode options, keyed by track key (never object identity); out-of-page
   *  selections are prepended so the trigger label still resolves. */
  protected readonly selectItems = computed<SelectDsItem<string>[]>(() => {
    const items = (this.searchResults() ?? []).map((record) => ({
      value: this.selectKey(record),
      label: this.displayEntity(record),
    }));
    const keys = new Set(items.map((i) => i.value));
    for (const record of this.selectedRecords()) {
      const key = this.selectKey(record);
      if (!keys.has(key)) {
        keys.add(key);
        items.unshift({ value: key, label: this.displayEntity(record) });
      }
    }
    return items;
  });

  protected readonly selectValue = computed<string | null>(() => {
    const value = this.value();
    return value == null ? null : this.selectKey(value);
  });

  protected readonly selectValues = computed<string[]>(() =>
    this.values().map((record) => this.selectKey(record))
  );

  protected readonly selectEmptyLabel = computed(() => {
    if (this.loading()) return 'Wird geladen …';
    if (this.error()) return 'Fehler beim Laden';
    return 'Keine Einträge gefunden';
  });

  protected selectLabel(definition: MasterdataEntityDefinition<TDto>): string | null {
    return this.showLabel() ? this.label() || definition.label : null;
  }

  onSelectOpened(): void {
    if (this.searchResults() === null && !this.loading()) this.searchFn('');
  }

  onSelectSearch(searchTerm: string): void {
    if (searchTerm === (this.currentTerm ?? '')) return;
    this.clearSelectSearchDebounce();
    this.abortInFlightSearch();
    this.selectSearchHandle = setTimeout(() => {
      this.selectSearchHandle = null;
      this.searchFn(searchTerm);
    }, SELECT_SEARCH_DEBOUNCE_MS);
  }

  onSelectValue(key: string | null): void {
    this.onSelected(key == null ? null : this.recordForKey(key));
  }

  onSelectValues(keys: string[] | null): void {
    const records = (keys ?? [])
      .map((key) => this.recordForKey(key))
      .filter((record): record is TDto => record !== null);
    this.values.set(records);
    this.pickedMany.emit(records);
  }

  private readonly selectKey = (record: TDto): string => String(this.trackOf(record));

  private selectedRecords(): TDto[] {
    if (this.multiple()) return this.values();
    const value = this.value();
    return value == null ? [] : [value];
  }

  private recordForKey(key: string): TDto | null {
    return (
      (this.searchResults() ?? []).find((r) => this.selectKey(r) === key) ??
      this.selectedRecords().find((r) => this.selectKey(r) === key) ??
      null
    );
  }

  private clearSelectSearchDebounce(): void {
    if (this.selectSearchHandle === null) return;
    clearTimeout(this.selectSearchHandle);
    this.selectSearchHandle = null;
  }

  focus(): void {
    const host = this.host.nativeElement as HTMLElement;
    (
      host.querySelector<HTMLElement>('input') ??
      host.querySelector<HTMLElement>('button')
    )?.focus();
  }

  onSearchClosed(): void {
    this.resetSearchState();
  }

  private resetSearchState(): void {
    this.clearSelectSearchDebounce();
    this.abortInFlightSearch();
    this.searchResults.set(null);
    this.totalCount.set(null);
    this.hasMore.set(false);
    this.loading.set(false);
    this.error.set(false);
    this.currentTerm = '';
    this.offset = 0;
    this.autoSelectArmed = false;
  }

  private entityKey(entity: TDto): string {
    const definition = this.definition();
    return String(
      definition.trackWith?.(entity) ?? (entity as { id?: unknown }).id ?? ''
    );
  }

  /** Fetches the record for `[entityId]` and writes it to `value`; matched
   *  client-side by identity, failures are silent. */
  private async resolveEntityId(
    definition: MasterdataEntityDefinition<TDto>,
    id: string | number,
    seq: number
  ): Promise<void> {
    try {
      const filter =
        definition.idFilter?.(id) ??
        (typeof id === 'number'
          ? { type: 'numberEquals', name: 'id', value: id }
          : { type: 'stringEquals', name: 'id', value: id });
      const results = await this.masterdata.search(definition, null, {
        limit: 2,
        filter,
      });
      if (seq !== this.resolveIdSeq) return;
      const match = results.find((r) => this.entityKey(r) === String(id)) ?? null;
      if (match) this.value.set(match);
    } catch {}
  }

  /** Default bookmark mappers: the backend body accepts fixed fields only, so the
   *  record is JSON-stashed into the freeform slot (`info` / `description`). */
  readonly defaultToFavoriteBody = (entity: TDto): FavoriteBookmarkBody => ({
    key: this.entityKey(entity),
    title: this.definition().displayWith(entity),
    info: JSON.stringify(entity),
  });

  readonly defaultToLastViewedBody = (entity: TDto): LastViewedEntityBookmarkBody => ({
    key: this.entityKey(entity),
    title: this.definition().displayWith(entity),
    description: JSON.stringify(entity),
  });

  /** Never returns null — the shell renders the mapped rows unfiltered. */
  readonly defaultFromBookmarkBody: MasterdataFromBookmark<TDto> = (body) => {
    try {
      const raw = body?.info ?? body?.description;
      return raw ? (JSON.parse(raw) as TDto) : ({} as TDto);
    } catch {
      return {} as TDto;
    }
  };
}
